import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { AddressEditor, Editor } from '@/components/account/record/FactRow.jsx';
import { setV3PolicyNumber } from '@/services/v3.service.js';
import OpenItem from './OpenItem.jsx';
import { NOT_RECORDED, POLICY_NUMBER_ERROR_WORDS, SAVED_REREADING, word } from './words.js';
import { anchorFor, editorKind, senderFor, PAGE_DOORS } from './doors.js';

// ONE FACT LINE ON THE V3 PAGE: label, the value or the NOT_RECORDED word,
// and, when an AIRgus item names this fact, the item ON THIS ROW with the
// field in place (an address editor, a date, a number, the policy number
// input). The page tells the line which items are its own (by fact_ref); the
// line derives no state. The field posts through doors.js, which only names
// writers that already exist; a fact with no door renders the state word and
// says what the door is. The row carries anchorFor(fact_ref) as its id, so a
// remedy that names this fact can point at it.

// THE POLICY NUMBER DOOR (stage D2). Posts through v3.service to the v3
// policies writer, renders the SERVER'S value (the row re-read after the
// write, never the typed string), then asks the page to re-read the account,
// which is what clears the AIRgus item: the reader stops emitting it, the
// client removes nothing. An error is the server's code, rendered inline
// through POLICY_NUMBER_ERROR_WORDS, never a toast alone. When the location
// hash names this row (a remedy link), the input takes focus.
function PolicyNumberInput({ policyId, anchor, onSaved }) {
  const [v, setV] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);
  const input = useRef(null);
  useEffect(() => {
    const focusIfNamed = () => { if (anchor && window.location.hash === `#${anchor}`) input.current?.focus(); };
    focusIfNamed();
    window.addEventListener('hashchange', focusIfNamed);
    return () => window.removeEventListener('hashchange', focusIfNamed);
  }, [anchor]);
  const submit = async (e) => {
    e.preventDefault();
    if (!v.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await setV3PolicyNumber(policyId, v);
      setSaved(res?.policy?.policy_number ?? null);
      onSaved?.();
    } catch (err) { setError(err?.code || err?.message || 'save failed'); }
    finally { setSaving(false); }
  };
  if (saved) {
    return (
      <span className="flex flex-wrap items-center gap-2 text-sm" data-policy-number-saved>
        <span className="font-mono text-text">{saved}</span>
        <span className="text-xs text-text-dim">{SAVED_REREADING}</span>
      </span>
    );
  }
  return (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-2" data-policy-number-input>
      <Input ref={input} value={v} onChange={(e) => setV(e.target.value)} placeholder="Policy number" aria-label="Policy number" className="min-h-[36px] w-56 font-mono text-sm" disabled={saving} />
      <button type="submit" disabled={saving} className="min-h-[36px] rounded-card border border-accent bg-accent px-3 text-sm font-medium text-white disabled:opacity-60">Save</button>
      {saving ? <span className="text-xs text-text-dim">saving</span> : null}
      {error ? <span className="basis-full text-sm text-danger" data-save-error={error}>{word(POLICY_NUMBER_ERROR_WORDS, error)}</span> : null}
    </form>
  );
}

function InPlaceEditor({ fact_ref, ctx, label, value, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);
  if (fact_ref.subject === 'term' && fact_ref.field === 'policy_number') return <PolicyNumberInput policyId={fact_ref.id} anchor={anchorFor(fact_ref)} onSaved={onSaved} />;
  const { sender, label: doorWord } = senderFor(fact_ref, ctx);
  if (!sender) return <span className="text-sm text-text-dim" data-state-word>{NOT_RECORDED}{doorWord ? <span className="ml-2 text-xs">{doorWord}</span> : null}</span>;
  if (!open) return <button type="button" className="inline-flex min-h-[36px] items-center text-sm text-text-dim underline-offset-2 hover:underline" onClick={() => setOpen(true)}>{NOT_RECORDED}, add it</button>;
  const commit = async (v) => {
    if (fact_ref.field !== 'address' && String(v ?? '').trim() === '') return;
    setSaving(true);
    try {
      const res = await sender(v);
      if (res && res.success === false) throw new Error(res.error || 'Save failed');
      for (const w of (res?.warnings || [])) toast.info(typeof w === 'string' ? w : (w.message || String(w)));
      toast.success(`${label} saved`);
      onSaved?.();
    } catch (e) { toast.error(e?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  if (fact_ref.field === 'address') return <AddressEditor parts={value || {}} onCommit={commit} onCancel={() => setOpen(false)} />;
  return (
    <span className="flex items-center gap-2">
      <Editor kind={editorKind(fact_ref.field)} value="" onCommit={commit} onCancel={() => setOpen(false)} autoFocus={false} />
      {saving ? <span className="text-xs text-text-dim">saving</span> : null}
    </span>
  );
}

export default function FactLine({ label, value, mono = false, items = [], fact_ref = null, ctx, editorValue, onSaved, remedyHref, children }) {
  // An item whose remedy IS this row's input (a PAGE_DOORS opener, set_policy_number) is hosted here even when the rule says it does not
  // know: the broker may well know the number, and stating it is the answer. Without this the legacy "we do not know whether the carrier
  // issued a number" item rendered "Add it: no door here" beside a row that had the door.
  const hostsEditor = (i) => !!(i.remedy && PAGE_DOORS[i.remedy.opener]);
  const own = items.filter((i) => i.unknown !== true || hostsEditor(i));
  const unknownOnes = items.filter((i) => i.unknown === true && !hostsEditor(i));
  const empty = value == null || value === '';
  const showEditor = fact_ref && own.length > 0;
  // The field on this row IS the remedy when it can post (the policy number
  // input posts through the v3 writer); the item then shows no second
  // remedy. A row whose door does not exist says NO_DOOR_HERE.
  const inline = !!showEditor && ((fact_ref.subject === 'term' && fact_ref.field === 'policy_number') || !!senderFor(fact_ref, ctx).sender);
  const key = fact_ref ? `${fact_ref.subject}:${fact_ref.id}:${fact_ref.field}` : undefined;
  return (
    <div id={fact_ref ? anchorFor(fact_ref) : undefined} className="flex flex-col gap-1 border-b border-border py-2 last:border-b-0 sm:grid sm:min-h-[44px] sm:grid-cols-[150px_minmax(0,1fr)] sm:items-start sm:gap-3" data-fact-ref={key} data-has-item={items.length ? 'true' : undefined}>
      <div className="text-xs text-text-muted sm:pt-1.5 sm:text-sm">{label}</div>
      <div className="min-w-0">
        {showEditor ? (
          <InPlaceEditor fact_ref={fact_ref} ctx={ctx} label={label} value={editorValue} onSaved={onSaved} />
        ) : empty ? (
          <span className="text-sm text-text-dim" data-state-word>{NOT_RECORDED}</span>
        ) : (
          <span className={`text-sm text-text ${mono ? 'font-mono' : ''}`}>{value}</span>
        )}
        {children}
        {own.map((it, i) => <OpenItem key={it.code + i} item={it} compact inline={inline} href={remedyHref?.(it)} />)}
        {unknownOnes.map((it, i) => <OpenItem key={'u' + it.code + i} item={it} compact />)}
      </div>
    </div>
  );
}
